/**
 * `Metric_Day` primitives — the calendar-day bucket every analytics number is
 * counted into.
 *
 * The Owner reads "yesterday" as her own day, so buckets are aligned to local
 * midnight in {@link OWNER_TIMEZONE} rather than to UTC. All conversions read
 * the local date parts of an instant through `Intl.DateTimeFormat`, so the UTC
 * offset in effect at that instant (EST or EDT) is applied by construction and
 * daylight-saving transitions need no special casing — the same technique used
 * by `calendar/booking-calendar.ts`.
 *
 * Pure and dependency-free so the backend (ingest, rollup, insights) and the
 * frontend (Insights tab) share one definition of a day.
 */
/**
 * The Owner's timezone. Every Metric_Day boundary is a local midnight here.
 * Single source of truth: `calendar/booking-calendar.ts` re-exports this as
 * `MODEL_TIMEZONE` so the two cannot drift.
 */
export const OWNER_TIMEZONE = "America/New_York";
/** Milliseconds in one civil day; only used for whole-day date arithmetic. */
const DAY_MS = 86_400_000;
const METRIC_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Cached formatters per timezone. `Intl.DateTimeFormat` construction is the
 * expensive part, and the ingest path formats once per event.
 */
const formatters = new Map();
function partsFormatter(timeZone) {
    let formatter = formatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone,
            hour12: false,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        formatters.set(timeZone, formatter);
    }
    return formatter;
}
/** The local wall-clock parts of `instantMs` in `timeZone`. */
function localParts(instantMs, timeZone) {
    const map = {};
    for (const part of partsFormatter(timeZone).formatToParts(new Date(instantMs))) {
        map[part.type] = part.value;
    }
    return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        // Some ICU versions render local midnight as hour 24 of the previous day.
        hour: map.hour === "24" ? 0 : Number(map.hour),
        minute: Number(map.minute),
        second: Number(map.second),
    };
}
/** The offset in ms of `timeZone` at the instant `atMs` (DST-aware). */
function tzOffsetMs(timeZone, atMs) {
    const p = localParts(atMs, timeZone);
    return (Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - atMs);
}
function pad2(value) {
    return value < 10 ? `0${value}` : String(value);
}
function pad4(value) {
    return String(value).padStart(4, "0");
}
/**
 * The Metric_Day containing `instantMs`, i.e. its local `YYYY-MM-DD` date in
 * `timeZone` using the UTC offset in effect at that instant (Req 6.2, 6.3).
 */
export function metricDayOf(instantMs, timeZone = OWNER_TIMEZONE) {
    if (!Number.isFinite(instantMs)) {
        throw new RangeError(`Not a finite instant: ${String(instantMs)}`);
    }
    const p = localParts(instantMs, timeZone);
    return `${pad4(p.year)}-${pad2(p.month)}-${pad2(p.day)}`;
}
/** Parses `day` into its civil year/month/day, throwing when malformed. */
function requireMetricDay(day) {
    const match = METRIC_DAY_PATTERN.exec(day);
    if (!match || !isMetricDay(day)) {
        throw new RangeError(`Not a Metric_Day (YYYY-MM-DD): ${String(day)}`);
    }
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        dayOfMonth: Number(match[3]),
    };
}
/**
 * Epoch ms of the first instant of `day` — local midnight in `timeZone`.
 *
 * Resolved by probing the offset twice: once at the naive UTC reading of the
 * date, then again at the corrected instant, so a day that begins on the far
 * side of a DST transition still lands on its own local midnight.
 */
export function metricDayStartMs(day, timeZone = OWNER_TIMEZONE) {
    const { year, month, dayOfMonth } = requireMetricDay(day);
    const naiveUtc = Date.UTC(year, month - 1, dayOfMonth);
    const firstOffset = tzOffsetMs(timeZone, naiveUtc);
    const secondOffset = tzOffsetMs(timeZone, naiveUtc - firstOffset);
    return naiveUtc - secondOffset;
}
/**
 * Epoch ms of the end of `day`, **exclusive**: the first instant of the next
 * Metric_Day. An instant belongs to `day` exactly when it is in
 * `[metricDayStartMs(day), metricDayEndMs(day))`, and record TTLs are anchored
 * to this instant so every record for a day expires together (Req 6.4, 6.5).
 */
export function metricDayEndMs(day, timeZone = OWNER_TIMEZONE) {
    return metricDayStartMs(addCivilDays(day, 1), timeZone);
}
/** The Metric_Day `delta` civil days after `day` (negative moves backwards). */
function addCivilDays(day, delta) {
    const { year, month, dayOfMonth } = requireMetricDay(day);
    const shifted = new Date(Date.UTC(year, month - 1, dayOfMonth) + delta * DAY_MS);
    return `${pad4(shifted.getUTCFullYear())}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}
/**
 * Every Metric_Day from `start` to `end` inclusive, ascending. Returns an empty
 * array when `start` is later than `end`, so an inverted range yields no days
 * rather than looping.
 */
export function metricDayRange(start, end) {
    requireMetricDay(start);
    requireMetricDay(end);
    if (start > end) {
        return [];
    }
    const days = [];
    let current = start;
    while (current <= end) {
        days.push(current);
        current = addCivilDays(current, 1);
    }
    return days;
}
/**
 * The `count`-day window ending on the Metric_Day containing `nowMs`, inclusive
 * of both ends: `count === 1` yields today for both `start` and `end`.
 */
export function lastNMetricDays(count, nowMs, timeZone = OWNER_TIMEZONE) {
    if (!Number.isInteger(count) || count < 1) {
        throw new RangeError(`Day count must be a positive integer: ${count}`);
    }
    const end = metricDayOf(nowMs, timeZone);
    return { start: addCivilDays(end, -(count - 1)), end };
}
/**
 * True iff `value` is a well-formed `YYYY-MM-DD` calendar date. Rejects dates
 * that do not exist (`2025-02-30`) as well as malformed strings, so a value
 * that passes can always be parsed by the functions above.
 */
export function isMetricDay(value) {
    if (typeof value !== "string") {
        return false;
    }
    const match = METRIC_DAY_PATTERN.exec(value);
    if (!match) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const dayOfMonth = Number(match[3]);
    if (month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) {
        return false;
    }
    const asUtc = new Date(Date.UTC(year, month - 1, dayOfMonth));
    return (asUtc.getUTCFullYear() === year &&
        asUtc.getUTCMonth() === month - 1 &&
        asUtc.getUTCDate() === dayOfMonth);
}
//# sourceMappingURL=metric-day.js.map